/**
 * Shared persona, narrative, and logo catalog for all Automatrix mockups.
 *
 * Used by: Automatrix dashboard mocks, Ext v5 mocks, Android phone mocks, iOS phone mocks.
 * The four mock swarms ran in parallel and each invented their own names — this module is the
 * final resolver so a reviewer sees THE SAME STORY across platforms.
 *
 * Narrative: Morgan Reyes (iCivics pilot teacher at Five Keys) is rostering 8th-grade
 * American Government (section 5A). Jaden Martinez is the student whose Automatrix
 * journey is depicted on both the web dashboard and the mobile port mocks.
 */

// ---------------------------------------------------------------------------
// PERSONAS
// ---------------------------------------------------------------------------
export const STUDENT = {
  full: 'Jaden Martinez',
  short: 'Jaden M.',
  initials: 'JM',
  username: 'jmartinez',                     // AchieveDXP username
  email: 'jmartinez@fivekeys.edu',
  khanUser: 'student.jmart',                 // Khan account (Layer 3 injected)
  khanEmail: 'student.jmart@fivekeys.edu',
  icivicsUser: 'jmartinez-5a',
  grade: 'Grade 8',
};

export const TEACHER = {
  full: 'Morgan Reyes',
  short: 'Mx. Reyes',                        // gender-neutral honorific — matches pilot
  initials: 'MR',
  username: 'mreyes',
  email: 'mreyes@fivekeys.edu',
};

export const CLASS = {
  name: '8th Grade American Government',
  section: '5A',
  displayName: '8th Grade American Government · 5A',
  code: 'CIV5A2',                            // joins with the iCivics pilot class code
  app: 'iCivics',
};

export const CLIENT = {
  name: 'Five Keys',
  longName: 'Five Keys Schools',
  domain: 'fivekeys.edu',
};

// ---------------------------------------------------------------------------
// PROGRESS / IMPACT POINTS (shared across Android, iOS, Ext v5 agent mode)
// ---------------------------------------------------------------------------
export const IMPACT = {
  total: 1280,
  weekDelta: 140,
  streakDays: 7,
  badges: 12,
  courses: 6,
  classesJoined: 3,
  masteryCount: 42,
};

// ---------------------------------------------------------------------------
// LOGO CATALOG — real CDN URLs from nucleos-frontend-assets (src-of-truth:
// /root/automatrix/nucleos-apps-complete.json). ReadTheory/LearnFree aren't in
// the Nucleos integration catalog, so we use their canonical brand logos hosted
// on partner sites.
// ---------------------------------------------------------------------------
const CDN = 'https://storage.googleapis.com/nucleos-frontend-assets/icons/application-integrations';

export const APP_LOGOS = {
  khan:        `${CDN}/KhanAcademy.png`,
  icivics:     `${CDN}/Icivics.png`,
  readtheory:  `${CDN}/ReadTheory.png`,      // present on CDN even though JSON lacks it
  learnfree:   `${CDN}/LearnFree.png`,
  w3schools:   `${CDN}/W3Schools.png`,
  careersafe:  `${CDN}/CareerSafe.png`,
  typingclub:  'https://static.typingclub.com/m/corp2/lessonplan-icons/typing1.png',
  duolingo:    `${CDN}/Duolingo.png`,
};

// Brand tint color per app — used for fallback tile bg when a logo doesn't load.
export const APP_COLORS = {
  khan:        '#14BF96',
  icivics:     '#F7931E',
  readtheory:  '#2C7BE5',
  learnfree:   '#5A67D8',
  w3schools:   '#04AA6D',
  careersafe:  '#E53935',
  typingclub:  '#FFC107',
  duolingo:    '#58CC02',
};

export const APP_NAMES = {
  khan: 'Khan Academy',
  icivics: 'iCivics',
  readtheory: 'ReadTheory',
  learnfree: 'LearnFree',
  w3schools: 'W3Schools',
  careersafe: 'CareerSafe',
  typingclub: 'TypingClub',
  duolingo: 'Duolingo',
};

// ---------------------------------------------------------------------------
// SHARED ACTIVITY TIMELINE ENTRIES — reused by Android/iOS Progress + Profile
// so a reviewer sees the same events on both devices.
// ---------------------------------------------------------------------------
export const TIMELINE_EVENTS = [
  {
    appKey: 'icivics', app: 'iCivics',
    title: 'Case won: Do I Have a Right?',
    day: 'Today', when: '10:42 AM', points: 45,
  },
  {
    appKey: 'khan', app: 'Khan Academy',
    title: 'Mastered: Solving linear equations',
    day: 'Today', when: '9:15 AM', points: 20,
  },
  {
    appKey: 'readtheory', app: 'ReadTheory',
    title: 'Quiz: Level 7 passage #18',
    day: 'Yesterday', when: '3:45 PM', points: 15,
  },
  {
    appKey: 'duolingo', app: 'Duolingo',
    title: 'Spanish · Unit 2 streak +1',
    day: 'Yesterday', when: '10:12 AM', points: 5,
  },
  {
    appKey: 'typingclub', app: 'TypingClub',
    title: 'Lesson 14 complete · 42 WPM',
    day: 'Apr 19', when: '4:30 PM', points: 4,
  },
];

// ---------------------------------------------------------------------------
// SHARED CLASS MEMBERSHIPS — reused by both Android and iOS Profile mocks.
// ---------------------------------------------------------------------------
export const CLASS_MEMBERSHIPS = [
  { name: '8th Grade American Government · 5A', teacher: 'Mx. Reyes', app: 'iCivics',     appKey: 'icivics',    code: 'CIV5A2' },
  { name: 'Algebra 1 — Period 3',                teacher: 'Mr. Diaz',  app: 'Khan Academy', appKey: 'khan',       code: 'KH3P92' },
  { name: 'Reading Workshop',                    teacher: 'Ms. Lee',   app: 'ReadTheory',   appKey: 'readtheory', code: 'RT42K8' },
];

// ---------------------------------------------------------------------------
// ACHIEVEMENTS — shared across iOS/Android Progress + Profile.
// ---------------------------------------------------------------------------
export const ACHIEVEMENTS = [
  { emoji: '🏆', label: 'First Case Won',  color: '#F7931E' },
  { emoji: '🔥', label: '7-Day Streak',    color: '#FF5722' },
  { emoji: '⭐', label: 'Math Apprentice', color: '#7B61FF' },
  { emoji: '📚', label: 'Reader · L5',     color: '#2C7BE5' },
];
